import { type Layer1Classification, type Layer2Classification } from "utils/gpuDetection";

import { type GuidanceItemKey, type GuidanceItemState, type GuidanceSituation } from "./guidanceModel";
import { type WatchdogBreachEvent } from "./performanceWatchdog";

type BuildGuidanceItemsInput = {
    isWindows: boolean;
    layer1: Layer1Classification;
    layer2: Layer2Classification;
    watchdogBreach?: WatchdogBreachEvent;
    watchdogQualityFloor?: WatchdogBreachEvent;
};

const BASE_ITEM_ORDER: GuidanceItemKey[] = [
    "browserAcceleration",
    "gpuPreference",
    "hags",
    "windowedGames",
    "tabsAndPower",
];

export function classifySituation({
    layer1,
    layer2,
    watchdogBreach,
    watchdogQualityFloor,
    isWindows = true,
}: Omit<BuildGuidanceItemsInput, "isWindows"> & { isWindows?: boolean }): GuidanceSituation | null {
    if (layer1.status === "acceleration-off") {
        return "A";
    }

    // Mac (and other non-Windows) only surfaces situation A. B/D/E are Windows GPU-preference
    // flows; Apple/Metal is treated as a discrete GPU and must not open the wizard.
    if (!isWindows) {
        return null;
    }

    if (layer2.status === "likely-igpu") {
        return "B";
    }

    if (layer2.status === "likely-discrete" && (watchdogBreach || watchdogQualityFloor)) {
        return "D";
    }

    if (layer2.status === "unknown") {
        return "E";
    }

    return null;
}

export function isGhostNudgeEligible({
    isWindows,
    layer2,
    situation,
}: {
    isWindows: boolean;
    layer2: Layer2Classification;
    situation: GuidanceSituation | null;
}): boolean {
    return isWindows && layer2.status === "likely-igpu" && situation !== "A";
}

export function buildGuidanceItems({
    isWindows,
    layer1,
    layer2,
    watchdogBreach,
    watchdogQualityFloor,
}: BuildGuidanceItemsInput): GuidanceItemState[] {
    const visibleItems = isWindows
        ? BASE_ITEM_ORDER
        : BASE_ITEM_ORDER.filter((item) => item !== "gpuPreference" && item !== "hags" && item !== "windowedGames");

    return visibleItems.map((item) => ({
        key: item,
        state: getItemState({ item, isWindows, layer1, layer2, watchdogBreach, watchdogQualityFloor }),
    }));
}

function getItemState({
    item,
    isWindows,
    layer1,
    layer2,
    watchdogBreach,
    watchdogQualityFloor,
}: {
    item: GuidanceItemKey;
    isWindows: boolean;
    layer1: Layer1Classification;
    layer2: Layer2Classification;
    watchdogBreach?: WatchdogBreachEvent;
    watchdogQualityFloor?: WatchdogBreachEvent;
}): GuidanceItemState["state"] {
    if (item === "browserAcceleration" && layer1.status === "ok") {
        return "verified";
    }

    return shouldEmphasize({ item, isWindows, layer1, layer2, watchdogBreach, watchdogQualityFloor })
        ? "emphasized"
        : "normal";
}

function shouldEmphasize({
    item,
    isWindows,
    layer1,
    layer2,
    watchdogBreach,
    watchdogQualityFloor,
}: {
    item: GuidanceItemKey;
    isWindows: boolean;
    layer1: Layer1Classification;
    layer2: Layer2Classification;
    watchdogBreach?: WatchdogBreachEvent;
    watchdogQualityFloor?: WatchdogBreachEvent;
}): boolean {
    if (item === "browserAcceleration") {
        return layer1.status === "acceleration-off";
    }

    if (item === "gpuPreference") {
        if (layer2.status === "likely-igpu") {
            return true;
        }
        return Boolean(watchdogQualityFloor) && layer2.status !== "likely-discrete";
    }

    if (item === "tabsAndPower") {
        return Boolean(watchdogBreach) || Boolean(watchdogQualityFloor);
    }

    if (item === "hags" || item === "windowedGames") {
        return isWindows && (layer1.status !== "ok" || Boolean(watchdogBreach) || Boolean(watchdogQualityFloor));
    }

    return false;
}
